import React, { createContext, useState, useEffect, useContext } from "react";
import { AuthContext } from "./AuthContext";

export const ThemeContext = createContext();

const STORAGE_KEY = "opspot-theme";

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else if (theme === "light") {
    root.classList.remove("dark");
  } else {
    root.classList.toggle("dark", window.matchMedia("(prefers-color-scheme: dark)").matches);
  }
}

export const ThemeProvider = ({ children }) => {
  const { authContext } = useContext(AuthContext);

  // Seed from localStorage immediately so there's no flash on load
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || "system";
  });

  // When the real saved preference arrives from the server, apply it
  // (authContext.user.themePreference starts null, becomes the real value after auth)
  useEffect(() => {
    const saved = authContext.user.themePreference;
    if (saved) {
      setThemeState(saved);
      localStorage.setItem(STORAGE_KEY, saved);
    }
  }, [authContext.user.themePreference]);

  // Apply dark class whenever theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // React to OS preference changes when set to "system"
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => document.documentElement.classList.toggle("dark", e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
